// RITA-1542 (Hauptticket RITA-901) — „PDF-Probe": die Server-PDF des aktuellen Editorstands in einem neuen Tab.
//
// Ablauf: Der neue Tab wird noch im Klick geöffnet (sonst blockiert der Browser das Fenster), dann wird die
// PDF mit den Beispielwerten der Platzhalter beim Server angefordert und als Blob-Adresse in den Tab geladen
// (seit RITA-1597 dieselben Werte wie die Seitenansicht: gewählter Beispielfall und Marke der Vorlage).
// Schlägt der Abruf fehl, wird der Tab geschlossen und der Hinweis in der Vertragsansicht gezeigt. Während
// des Abrufs ist der Knopf gesperrt; der Editor bleibt bedienbar. Es wird nichts gespeichert oder abgelegt.
import { useCallback, useEffect, useRef, useState } from 'react';
import { PDF_PROBE_FEHLER, vertragsvorlagePdfAbrufen, VertragsvorlagePdfFehler } from '../api/vertragsvorlagePdf';
import { Beispielauswahl, beispielwerte, STANDARD_BEISPIELFALL } from '../contractEditor/model/beispielfaelle';
import { speicherdokument } from '../contractEditor/model/speicherform';

type PdfProbeEditorstand = Parameters<typeof speicherdokument>[0];

export const PDF_PROBE_FENSTER_BLOCKIERT =
  'Der neue Tab für die PDF wurde vom Browser blockiert. Bitte Pop-ups für diese Seite erlauben und erneut versuchen.';
export const PDF_PROBE_WARTETEXT = 'PDF wird erstellt …';

/**
 * Beispielwerte aller bekannten Platzhalter (Entscheidung laut Ticket: die PDF-Probe zeigt Beispielwerte) –
 * für den gewählten Beispielfall und die Marke der Vorlage, ohne Angabe der Grundfall „Berater“ (RITA-1597).
 */
export const pdfProbeWerte = (beispiel?: Beispielauswahl): Record<string, string> => ({
  ...beispielwerte(beispiel?.fall ?? STANDARD_BEISPIELFALL, beispiel?.marke ?? null),
});

export interface PdfProbe {
  /**
   * Öffnet die PDF desselben bereinigten Editorstands wie beim Speichern; `titel` steht in den PDF-Metadaten
   * (Tab-Titel), `beispiel` sind Beispielfall und Marke der Seitenansicht.
   */
  oeffnen: (zustand: PdfProbeEditorstand, titel: string, beispiel?: Beispielauswahl) => Promise<void>;
  laeuft: boolean;
  fehler: string;
  fehlerSchliessen: () => void;
}

export interface PdfProbeAbhaengigkeiten {
  abrufen?: typeof vertragsvorlagePdfAbrufen;
  fensterOeffnen?: () => Window | null;
}

export default function usePdfProbe(abhaengigkeiten: PdfProbeAbhaengigkeiten = {}): PdfProbe {
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState('');
  const laeuftRef = useRef(false);
  const aktivRef = useRef(true);
  const abrufen = abhaengigkeiten.abrufen ?? vertragsvorlagePdfAbrufen;
  const fensterOeffnen = abhaengigkeiten.fensterOeffnen ?? (() => window.open('', '_blank'));

  useEffect(() => {
    aktivRef.current = true;
    return () => {
      aktivRef.current = false;
    };
  }, []);

  const oeffnen = useCallback(
    async (zustand: PdfProbeEditorstand, titel: string, beispiel?: Beispielauswahl) => {
      if (laeuftRef.current) return;
      const tab = fensterOeffnen();
      if (!tab) {
        setFehler(PDF_PROBE_FENSTER_BLOCKIERT);
        return;
      }
      try {
        tab.document.title = PDF_PROBE_WARTETEXT;
        if (tab.document.body) tab.document.body.textContent = PDF_PROBE_WARTETEXT;
      } catch (error) {
        // Ein fremdes oder bereits weitergeleitetes Fenster lässt sich nicht beschreiben; die PDF kommt trotzdem.
      }
      laeuftRef.current = true;
      setLaeuft(true);
      setFehler('');
      try {
        // RITA-1561 V33: Größenprüfung und PDF verwenden dieselbe Speicherform; der Editor bleibt unverändert.
        const blob = await abrufen({ dokument: speicherdokument(zustand), werte: pdfProbeWerte(beispiel), titel });
        // Die Blob-Adresse bleibt gültig, solange diese Seite offen ist (Neuladen des PDF-Tabs bleibt möglich);
        // der Browser gibt sie mit dieser Seite frei. Eine vorzeitige Freigabe bräche den PDF-Tab beim Neuladen.
        tab.location.href = URL.createObjectURL(blob);
      } catch (error) {
        tab.close();
        if (aktivRef.current) {
          setFehler(error instanceof VertragsvorlagePdfFehler ? error.message : PDF_PROBE_FEHLER.allgemein);
        }
      } finally {
        laeuftRef.current = false;
        if (aktivRef.current) setLaeuft(false);
      }
    },
    [abrufen, fensterOeffnen],
  );

  const fehlerSchliessen = useCallback(() => setFehler(''), []);

  return { oeffnen, laeuft, fehler, fehlerSchliessen };
}
